/**
 * @fileoverview Privacy Policy page.
 * @description Placeholder legal copy (see the on-page disclaimer) reachable from AuthLayout's
 * footer and, once contact-support is reached, from its own "Contact Support" cross-link.
 * Composes LegalLayout with LegalDocCard for the shared header/footer and card chrome.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'

import LinkButton from '@/shared/components/atoms/LinkButton'
import LegalLayout from '@/shared/components/layouts/LegalLayout'
import LegalDocCard from '@/shared/components/molecules/LegalDocCard'
import { ROUTES } from '@/shared/constants/routes.constants'

const H2 = 'mt-8 mb-3 text-[17px] font-bold leading-snug text-ink first:mt-0'
const P = 'mb-3.5 text-[14.5px] leading-[1.75] text-slate'
const UL = 'mb-3.5 list-disc space-y-1.5 pl-5.5'
const LI = 'text-[14.5px] leading-[1.75] text-slate'

/**
 * @component
 */
function PrivacyPolicyPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()

  return (
    <LegalLayout>
      <LegalDocCard
        title={t('privacyPolicy.title')}
        updatedLabel={t('privacyPolicy.updated')}
        disclaimer={
          <>
            <strong className="font-semibold text-ink">{t('privacyPolicy.disclaimerBold')}</strong>{' '}
            {t('privacyPolicy.disclaimerRest')}
          </>
        }
      >
        <h2 className={H2}>{t('privacyPolicy.sections.introduction.title')}</h2>
        <p className={P}>{t('privacyPolicy.sections.introduction.body1')}</p>
        <p className={P}>{t('privacyPolicy.sections.introduction.body2')}</p>

        <h2 className={H2}>{t('privacyPolicy.sections.collect.title')}</h2>
        <p className={P}>{t('privacyPolicy.sections.collect.intro')}</p>
        <ul className={UL}>
          <li className={LI}>{t('privacyPolicy.sections.collect.account')}</li>
          <li className={LI}>{t('privacyPolicy.sections.collect.authentication')}</li>
          <li className={LI}>{t('privacyPolicy.sections.collect.usage')}</li>
          <li className={LI}>{t('privacyPolicy.sections.collect.device')}</li>
        </ul>
        <p className={P}>{t('privacyPolicy.sections.collect.outro')}</p>

        <h2 className={H2}>{t('privacyPolicy.sections.howWeUse.title')}</h2>
        <ul className={UL}>
          <li className={LI}>{t('privacyPolicy.sections.howWeUse.item1')}</li>
          <li className={LI}>{t('privacyPolicy.sections.howWeUse.item2')}</li>
          <li className={LI}>{t('privacyPolicy.sections.howWeUse.item3')}</li>
          <li className={LI}>{t('privacyPolicy.sections.howWeUse.item4')}</li>
          <li className={LI}>{t('privacyPolicy.sections.howWeUse.item5')}</li>
        </ul>

        <h2 className={H2}>{t('privacyPolicy.sections.sharing.title')}</h2>
        <p className={P}>{t('privacyPolicy.sections.sharing.intro')}</p>
        <ul className={UL}>
          <li className={LI}>{t('privacyPolicy.sections.sharing.item1')}</li>
          <li className={LI}>{t('privacyPolicy.sections.sharing.item2')}</li>
          <li className={LI}>{t('privacyPolicy.sections.sharing.item3')}</li>
        </ul>

        <h2 className={H2}>{t('privacyPolicy.sections.security.title')}</h2>
        <p className={P}>{t('privacyPolicy.sections.security.body')}</p>

        <h2 className={H2}>{t('privacyPolicy.sections.retention.title')}</h2>
        <p className={P}>{t('privacyPolicy.sections.retention.body')}</p>

        <h2 className={H2}>{t('privacyPolicy.sections.rights.title')}</h2>
        <p className={P}>
          {t('privacyPolicy.sections.rights.bodyPrefix')}{' '}
          <LinkButton size="sm" onClick={() => void navigate(ROUTES.CONTACT_SUPPORT)}>
            {t('legalNav.contactSupport')}
          </LinkButton>{' '}
          {t('privacyPolicy.sections.rights.bodySuffix')}
        </p>

        <h2 className={H2}>{t('privacyPolicy.sections.cookies.title')}</h2>
        <p className={P}>{t('privacyPolicy.sections.cookies.body')}</p>

        <h2 className={H2}>{t('privacyPolicy.sections.changes.title')}</h2>
        <p className={P}>{t('privacyPolicy.sections.changes.body')}</p>

        <h2 className={H2}>{t('privacyPolicy.sections.contact.title')}</h2>
        <p className={P}>
          {t('privacyPolicy.sections.contact.bodyPrefix')}{' '}
          <a
            href={`mailto:${t('privacyPolicy.sections.contact.emailLabel')}`}
            className="font-medium text-primary underline"
          >
            {t('privacyPolicy.sections.contact.emailLabel')}
          </a>{' '}
          {t('privacyPolicy.sections.contact.bodyMiddle')}{' '}
          <LinkButton size="sm" onClick={() => void navigate(ROUTES.CONTACT_SUPPORT)}>
            {t('legalNav.contactSupport')}
          </LinkButton>{' '}
          {t('privacyPolicy.sections.contact.bodySuffix')}
        </p>
      </LegalDocCard>
    </LegalLayout>
  )
}

export default PrivacyPolicyPage
